import React, { useEffect, useRef } from 'react';
import { Slide } from '@/components/slides/Slide.tsx';
import { Slide as SlideType } from '@/eventConfig/types.ts';

import { SummaryFormsDE } from '@/components/slides/DataSummary/de/SummaryFormsDE';
import { SummaryFormsGB } from '@/components/slides/DataSummary/gb/SummaryFormsGB';
import { SummaryFormsCL } from '@/components/slides/DataSummary/cl/SummaryFormsCL';
import { SummaryFormsIN } from '@/components/slides/DataSummary/in/SummaryFormsIN';
import { SummaryFormsPL } from '@/components/slides/DataSummary/pl/SummaryFormsPL';
import { SummaryFormsUS } from '@/components/slides/DataSummary/us/SummaryFormsUS';
import { SummaryFormsZA } from '@/components/slides/DataSummary/za/SummaryFormsZA';
import { ConfirmDataEntryCheckboxDE } from '@/components/slides/DataSummary/de/ConfirmDataEntryCheckboxDE.tsx';
import { ConfirmDataEntryCheckboxGB } from '@/components/slides/DataSummary/gb/ConfirmDataEntryCheckboxGB.tsx';
import { ConfirmDataEntryCheckboxCL } from '@/components/slides/DataSummary/cl/ConfirmDataEntryCheckboxCL.tsx';
import { ConfirmDataEntryCheckboxIN } from '@/components/slides/DataSummary/in/ConfirmDataEntryCheckboxIN.tsx';
import { ConfirmDataEntryCheckboxUS } from '@/components/slides/DataSummary/us/ConfirmDataEntryCheckboxUS.tsx';
import { ConfirmDataEntryCheckboxPL } from '@/components/slides/DataSummary/pl/ConfirmDataEntryCheckboxPL.tsx';
import { ConfirmDataEntryCheckboxZA } from '@/components/slides/DataSummary/za/ConfirmDataEntryCheckboxZA.tsx';

interface Props {
  slide: SlideType;
  className?: string;
}

const summaryFormsMap: Record<string, React.FC> = {
  DE: SummaryFormsDE,
  GB: SummaryFormsGB,
  CL: SummaryFormsCL,
  IN: SummaryFormsIN,
  PL: SummaryFormsPL,
  US: SummaryFormsUS,
  ZA: SummaryFormsZA,
};

const confirmCheckboxMap: Record<string, React.FC<{ slide: SlideType }>> = {
  DE: ConfirmDataEntryCheckboxDE,
  GB: ConfirmDataEntryCheckboxGB,
  CL: ConfirmDataEntryCheckboxCL,
  IN: ConfirmDataEntryCheckboxIN,
  PL: ConfirmDataEntryCheckboxPL,
  US: ConfirmDataEntryCheckboxUS,
  ZA: ConfirmDataEntryCheckboxZA,
};

export const DataSummarySlide = ({ slide, ...rest }: Props) => {
  const country = slide.country ?? '';
  const SummaryFormComponent = summaryFormsMap[country];
  const ConfirmCheckboxComponent = confirmCheckboxMap[country];
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleArrowScroll = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }

      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
        return;
      }

      const target = event.target;
      if (target instanceof HTMLElement) {
        const tagName = target.tagName;
        if (target.isContentEditable || tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') {
          return;
        }
      }

      const amount = event.key === 'ArrowDown' ? 80 : -80;
      const contentContainer = wrapperRef.current?.closest('.content');
      if (contentContainer instanceof HTMLElement && contentContainer.scrollHeight > contentContainer.clientHeight) {
        event.preventDefault();
        event.stopPropagation();
        contentContainer.scrollBy({ top: amount, behavior: 'smooth' });
        return;
      }

      const page = document.scrollingElement;
      if (page) {
        event.preventDefault();
        event.stopPropagation();
        window.scrollBy({ top: amount, behavior: 'smooth' });
      }
    };

    window.addEventListener('keydown', handleArrowScroll, { capture: true });

    return () => {
      window.removeEventListener('keydown', handleArrowScroll, { capture: true });
    };
  }, []);

  return (
    <Slide slide={slide} {...rest}>
      <div className="data-summary-wrapper" ref={wrapperRef}>
        {SummaryFormComponent && <SummaryFormComponent />}
        {ConfirmCheckboxComponent && <ConfirmCheckboxComponent slide={slide} />}
      </div>
    </Slide>
  );
};
